import { useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useDispatch } from "react-redux";
import { firestore } from "lib/firebase/firebase";
import { brandingPackageSchema, BrandingPackageSchemaType, BrandingScope } from "types/branding";
import { applyBranding, resetToDefault, setStatus } from "store/reducers/branding";
import { UserOrg } from "types/auth";
import { getInternalAppType } from "utils/brandingUtils";

// One owner resolves visual branding. Stored user/org identity is deliberately not
// passed to the resolver: web defaults follow the host, native defaults the build.
export function useOrgBranding(userOrg: UserOrg | null | undefined, user: unknown) {
  const dispatch = useDispatch();
  const internalAppType = getInternalAppType({ pathOrHost: window.location.hostname });
  const orgId = userOrg?.id;
  const signedIn = !!user;
  const subscriptionPackageId = userOrg?.subscription?.brandingPackageId;
  const orgPackageId = userOrg?.defaultBrandingPackageId;
  const audienceMode = new URLSearchParams(window.location.search).has("audience");
  const urlPackageId = new URLSearchParams(window.location.search).get("bpid")?.trim();

  useEffect(() => {
    let active = true;
    const unsubscribers: Array<() => void> = [];
    const candidates: Array<{ id: string; scope: BrandingScope }> = [];
    if (audienceMode && urlPackageId) candidates.push({ id: urlPackageId, scope: "url" });
    if (!audienceMode && signedIn && subscriptionPackageId) candidates.push({ id: subscriptionPackageId, scope: "sub" });
    if (!audienceMode && signedIn && orgPackageId) candidates.push({ id: orgPackageId, scope: "org" });
    if (!audienceMode && urlPackageId) candidates.push({ id: urlPackageId, scope: "url" });
    const results = candidates.map(() => ({
      raw: null as BrandingPackageSchemaType | null,
      pending: true,
      error: undefined as string | undefined,
    }));

    function publish() {
      if (!active) return;
      // Recompute from current sources, including when a package is removed or fails.
      dispatch(resetToDefault({ internalAppType }));
      const selected = results.findIndex((result) => result.raw !== null);
      if (selected >= 0) {
        dispatch(applyBranding({
          scope: candidates[selected].scope,
          brandingPackageId: candidates[selected].id,
          raw: results[selected].raw,
          internalAppType,
        }));
      }
      const relevant = results.slice(0, selected < 0 ? results.length : selected + 1);
      const error = relevant.find((result) => result.error)?.error;
      if (error) dispatch(setStatus({ status: "error", error }));
      else if (relevant.some((result) => result.pending)) dispatch(setStatus({ status: "loading" }));
    }

    publish();
    candidates.forEach((candidate, index) => {
      const fail = (error: string) => {
        results[index] = { raw: null, pending: false, error };
        publish();
      };
      unsubscribers.push(onSnapshot(
        doc(firestore, `data/branding/schemes/${candidate.id}`),
        (snap) => {
          if (!active) return;
          if (!snap.exists()) return fail("Branding package not found");
          const parsed = brandingPackageSchema.safeParse({ ...snap.data(), id: snap.id });
          if (!parsed.success) return fail(parsed.error.message);
          results[index] = { raw: parsed.data, pending: false, error: undefined };
          publish();
        },
        (error) => fail(error.message),
      ));
    });
    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [dispatch, internalAppType, signedIn, orgId, subscriptionPackageId, orgPackageId, urlPackageId, audienceMode]);
}
